import { BLOCKS_API_URL, PROJECT_KEY } from "@/src/services/blocks/config";

/** The public proxy accepts a signed digest-only capability, never an app session. */
export const validDigestToken = (token: string) =>
  token.length >= 16 &&
  token.length <= 4096 &&
  /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token);

export function unsubscribeEndpoint(api: string, slug: string) {
  const url = new URL(api);
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.pathname !== "/" ||
    url.search ||
    url.hash ||
    ![
      "blocksapi.slsblx.com",
      "blocksapi.selise.club",
      "api.seliseblocks.com",
    ].includes(url.hostname) ||
    !/^[A-Za-z0-9_-]{1,160}$/.test(slug)
  )
    throw new Error("LINK_UNAVAILABLE");
  return `${url.origin}/logic/v4/proxy/gateway/${encodeURIComponent(slug)}`;
}

export function createDigestUnsubscribeClient(
  api: string,
  tenantKey: string,
  slug: string,
  fetcher: typeof fetch = fetch,
) {
  const endpoint = unsubscribeEndpoint(api, slug);
  if (!tenantKey) throw new Error("LINK_UNAVAILABLE");
  async function request(
    operation: "preview" | "confirm",
    token: string,
    signal?: AbortSignal,
  ) {
    if (!validDigestToken(token)) throw new Error("LINK_UNAVAILABLE");
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    signal?.addEventListener("abort", abort, { once: true });
    const timeout = setTimeout(abort, 10000);
    try {
      const response = await fetcher(
        operation === "preview"
          ? `${endpoint}/preview/${encodeURIComponent(token)}`
          : `${endpoint}/confirm`,
        {
          method: operation === "preview" ? "GET" : "POST",
          credentials: "omit",
          cache: "no-store",
          redirect: "error",
          referrerPolicy: "no-referrer",
          headers: {
            "x-blocks-key": tenantKey,
            ...(operation === "confirm"
              ? { "content-type": "application/json" }
              : {}),
          },
          ...(operation === "confirm"
            ? { body: JSON.stringify({ token }) }
            : {}),
          signal: controller.signal,
        },
      );
      if (!response.ok) throw new Error("LINK_UNAVAILABLE");
      const result = await response.json();
      if (
        result?.schemaVersion !== 1 ||
        (operation === "preview"
          ? result.canConfirm !== true
          : result.confirmed !== true)
      )
        throw new Error("LINK_UNAVAILABLE");
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener("abort", abort);
    }
  }
  return {
    preview: (token: string, signal?: AbortSignal) =>
      request("preview", token, signal),
    confirm: (token: string, signal?: AbortSignal) =>
      request("confirm", token, signal),
  };
}

export function publicUnsubscribeClient() {
  const slug = process.env.EXPO_PUBLIC_FOLLOWING_UNSUBSCRIBE_PROXY_SLUG ?? "";
  return createDigestUnsubscribeClient(BLOCKS_API_URL, PROJECT_KEY, slug);
}
